/**
 * Where to send someone once they have signed in: the page they were trying
 * to open. Carried two ways, because sign-in does not always stay in this
 * page: as router state (`from`) for the password form, and in
 * sessionStorage for the magic-link and Google round trips, which leave the
 * app and come back to a fixed URL.
 *
 * Only a same-origin path is ever followed, never the auth page itself, so a
 * crafted `from` cannot send anyone off-site or into a sign-in loop.
 */

const STORAGE_KEY = 'unipostudio.returnTo';
/** A sign-in abandoned for longer than this no longer decides where the next one lands. */
const MAX_AGE_MS = 30 * 60 * 1000;
const AUTH_PATH = '/auth';
const PROBE_ORIGIN = 'https://return-path.invalid';

/** The path itself when it is a safe same-origin destination, else null. */
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/')) return null;
  // `//evil.example` and `/\evil.example` are protocol-relative to a browser.
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  let url: URL;
  try {
    url = new URL(value, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  if (url.pathname === AUTH_PATH || url.pathname.startsWith(`${AUTH_PATH}/`)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Remembers a destination for after a sign-in that leaves the page. */
export function rememberReturnPath(path: string): void {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path: safe, at: Date.now() }));
  } catch {
    /* storage unavailable: the router state still carries it for the password form */
  }
}

/** The remembered destination, read once: it is cleared as it is read. */
export function takeReturnPath(now: number = Date.now()): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parsed.at !== 'number' || now - parsed.at > MAX_AGE_MS) return null;
    return safeReturnPath(parsed.path);
  } catch {
    return null;
  }
}

/** Forgets a remembered destination, once it has been honoured another way. */
export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}
